/**
 * EducationScholarshipDetail — /education/scholarships/:slug
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Award, Calendar, MapPin, Mail, ExternalLink, Loader2, Video,
  ClipboardList, ShieldCheck,
} from "lucide-react";
import { http } from "@/lib/api";
import EducationCategoryNav from "@/components/EducationCategoryNav";
import VideoEmbed from "@/components/VideoEmbed";

export default function EducationScholarshipDetail() {
  const { slug } = useParams();
  const [s, setS] = useState(null);
  const [err, setErr] = useState(null);
  const [meta, setMeta] = useState(null);

  useEffect(() => { http.get("/education/meta").then(r => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    http.get(`/education/scholarships/${slug}`).then(r => setS(r.data)).catch(e => setErr(e?.response?.data?.detail || "Not found"));
  }, [slug]);

  if (err) return (
    <><EducationCategoryNav />
      <div className="max-w-3xl mx-auto px-4 py-12 text-center">
        <h1 className="font-display text-2xl mb-2">Scholarship not found</h1>
        <p className="opacity-70">{err}</p>
        <Link to="/education/scholarships" className="wofy-link inline-block mt-4">← Back</Link>
      </div>
    </>
  );
  if (!s) return <><EducationCategoryNav /><div className="max-w-3xl mx-auto px-4 py-12 opacity-60 inline-flex gap-2"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div></>;

  const categoryLabel = meta?.scholarship_categories?.find(c => c.slug === s.category)?.label || s.category;
  const levelLabel    = meta?.institution_levels?.find(l => l.slug === s.level)?.label;

  return (
    <>
      <EducationCategoryNav />
      <article className="max-w-4xl mx-auto px-4 py-8" data-testid="scholarship-detail">
        {s.cover_image_url && (
          <div className="w-full h-56 rounded-xl overflow-hidden mb-6 bg-black/5"
               style={{ backgroundImage: `url(${s.cover_image_url})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        )}

        <div className="overline mb-2"><Award className="w-3 h-3 inline mr-1" />{categoryLabel}{levelLabel && ` · ${levelLabel}`}</div>
        <h1 className="font-display text-4xl sm:text-5xl leading-tight mb-2">{s.name}</h1>
        <div className="text-sm opacity-80 mb-4">by <b>{s.provider}</b></div>

        <div className="flex flex-wrap gap-4 text-sm opacity-80 mb-6">
          {s.application_deadline && (
            <span className="inline-flex items-center gap-1"><Calendar className="w-4 h-4" />
              Deadline: {s.application_deadline}
            </span>
          )}
          {(s.country || s.state) && (
            <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4" />
              {[s.state, s.country].filter(Boolean).join(", ")}
            </span>
          )}
          {s.amount_summary && (
            <span className="inline-flex items-center gap-1">Amount · {s.amount_summary}</span>
          )}
        </div>

        {s.description && (
          <section className="mb-8">
            <p className="whitespace-pre-line leading-relaxed opacity-90">{s.description}</p>
          </section>
        )}

        {s.eligibility && (
          <section className="mb-8">
            <h2 className="font-display text-2xl mb-2 inline-flex items-center gap-2">
              <ShieldCheck className="w-5 h-5" />Eligibility
            </h2>
            <p className="whitespace-pre-line text-sm opacity-90 leading-relaxed">{s.eligibility}</p>
          </section>
        )}

        {s.requirements && (
          <section className="mb-8">
            <h2 className="font-display text-2xl mb-2 inline-flex items-center gap-2">
              <ClipboardList className="w-5 h-5" />What you need
            </h2>
            <p className="whitespace-pre-line text-sm opacity-90 leading-relaxed">{s.requirements}</p>
          </section>
        )}

        {s.video_links?.length > 0 && (
          <section className="mb-8" data-testid="scholarship-videos">
            <h2 className="font-display text-2xl mb-3 inline-flex items-center gap-2">
              <Video className="w-5 h-5" />Videos
            </h2>
            <div className="grid gap-3 sm:grid-cols-2">
              {s.video_links.map((v, i) => <VideoEmbed key={i} video={v} />)}
            </div>
          </section>
        )}

        {(s.application_url || s.contact_email) && (
          <section className="wofy-card !p-4 flex flex-wrap items-center gap-3">
            {s.application_url && (
              <a href={s.application_url} target="_blank" rel="noreferrer" className="wofy-btn-primary inline-flex items-center gap-1.5" data-testid="apply-btn">
                Apply now <ExternalLink className="w-4 h-4" />
              </a>
            )}
            {s.contact_email && (
              <a href={`mailto:${s.contact_email}`} className="inline-flex items-center gap-1 wofy-link text-sm">
                <Mail className="w-4 h-4" />{s.contact_email}
              </a>
            )}
          </section>
        )}
      </article>
    </>
  );
}
